import * as Api from '@ViewModels';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { animated, useSpring } from '@react-spring/web';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { isOptimisticActionItem } from '../../../../queries/ActionItem/useActionItemMutations';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Pill } from '../../../components-v2/Pill';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';
import { GripVerticalIcon } from '../../../components/svgs/icons/GripVerticalIcon';
import { NotepadTextIcon } from '../../../components/svgs/icons/NotepadTextIcon';
import { getActionItemSecondaryText, getActionItemTitle } from '../utils/actionItemText';
import { getPriority } from '../utils/priority';
import { IDueDateValue } from '../utils/buckets';
import { AssigneeMenu } from './AssigneeMenu';
import { ContactChips } from './ContactChips';
import { DueChip } from './DueChip';
import { PriorityFlag } from './PriorityFlag';

export const COMPLETE_ANIMATION_MS = 400;

export const ACTION_ITEM_DRAG_TYPE = 'action-item';

export interface IActionItemRowCallbacks {
	onComplete: (item: Api.IActionItem) => void;
	onReschedule: (item: Api.IActionItem, value: IDueDateValue) => void;
	onSetPriority: (item: Api.IActionItem, priority: Api.ActionItemPriority) => void;
	onReassign: (item: Api.IActionItem, user: Api.IUser) => void;
	onOpen: (item: Api.IActionItem) => void;
	onEdit: (item: Api.IActionItem) => void;
	onDelete: (item: Api.IActionItem) => void;
	onFocus: (item: Api.IActionItem) => void;
}

interface IProps extends IActionItemRowCallbacks {
	item: Api.IActionItem;
	/** Position in the list, used to stagger the entrance animation. */
	index?: number;
	selected: boolean;
	selectionActive: boolean;
	onToggleSelected: (id: string, withRange: boolean) => void;
}

const ENTRANCE_STAGGER_MS = 20;
const ENTRANCE_MAX_DELAY_MS = 200;

export const ActionItemRow: React.FC<IProps> = ({
	item,
	index = 0,
	selected,
	selectionActive,
	onToggleSelected,
	onComplete,
	onReschedule,
	onSetPriority,
	onReassign,
	onOpen,
	onEdit,
	onDelete,
	onFocus,
}) => {
	const rowRef = React.useRef<HTMLDivElement>(null);
	const [completing, setCompleting] = React.useState(false);
	const [dragging, setDragging] = React.useState(false);
	const pending = isOptimisticActionItem(item);
	const isKeepInTouch = !!item.keepInTouchReference;
	const fromNote = !!item.associatedNote?.id;
	const title = getActionItemTitle(item);
	const secondary = getActionItemSecondaryText(item);
	const itemPriority = getPriority(item);

	const spring = useSpring({
		opacity: completing ? 0 : 1,
		x: completing ? 16 : 0,
		config: { tension: 260, friction: 26 },
	});

	React.useEffect(() => {
		const element = rowRef.current;
		if (!element || pending) {
			return;
		}
		return draggable({
			element,
			getInitialData: () => ({ type: ACTION_ITEM_DRAG_TYPE, id: item.id }),
			onDragStart: () => setDragging(true),
			onDrop: () => setDragging(false),
		});
	}, [item.id, pending]);

	React.useEffect(() => {
		if (!completing) {
			return;
		}
		const timer = window.setTimeout(() => onComplete(item), COMPLETE_ANIMATION_MS);
		return () => window.clearTimeout(timer);
	}, [completing, item, onComplete]);

	return (
		<animated.div
			ref={rowRef}
			data-testid={`action-item-row-${item.id}`}
			className={css(
				styleSheet.row,
				selected && styleSheet.rowSelected,
				dragging && styleSheet.rowDragging,
				pending && styleSheet.rowPending
			)}
			style={{
				opacity: spring.opacity,
				transform: spring.x.to(x => `translateX(${x}px)`),
				animationDelay: `${Math.min(index * ENTRANCE_STAGGER_MS, ENTRANCE_MAX_DELAY_MS)}ms`,
			}}
			onClick={() => !pending && onOpen(item)}
		>
			<div className={css(styleSheet.selectCell, (selectionActive || selected) && styleSheet.selectCellVisible)}>
				<Checkbox
					aria-label={`Select ${title}`}
					checked={selected}
					disabled={pending}
					onClick={event => {
						event.stopPropagation();
						onToggleSelected(item.id, (event as React.MouseEvent).shiftKey);
					}}
				/>
			</div>
			<span className={css(styleSheet.grip)} data-slot='row-grip' title='Drag to another day' aria-hidden>
				<GripVerticalIcon width={14} height={14} />
			</span>
			<div className={css(styleSheet.completeCell)} onClick={event => event.stopPropagation()}>
				<Checkbox
					size='lg'
					aria-label={`Complete ${title}`}
					checked={completing}
					disabled={pending || completing}
					styles={[
						styleSheet.completeCheckbox,
						itemPriority === 'High' && styleSheet.completeHigh,
						itemPriority === 'Medium' && styleSheet.completeMedium,
						itemPriority === 'Low' && styleSheet.completeLow,
					]}
					onCheckedChange={checked => {
						if (checked) {
							setCompleting(true);
						}
					}}
				/>
			</div>
			<div className={css(styleSheet.textCell)}>
				<span className={css(styleSheet.title, completing && styleSheet.titleCompleting)}>{title}</span>
				{secondary ? <span className={css(styleSheet.secondary)}>{secondary}</span> : null}
			</div>
			<div className={css(styleSheet.metaCell)}>
				{isKeepInTouch ? <Pill variant='tag' color='orange' size='small' label='Keep in touch' /> : null}
				{fromNote ? (
					<span className={css(styleSheet.noteIndicator)} role='img' aria-label='From a note' title='From a note'>
						<NotepadTextIcon width={16} height={16} />
					</span>
				) : null}
				<ContactChips item={item} />
				<DueChip item={item} disabled={pending} onChange={value => onReschedule(item, value)} />
				<PriorityFlag
					priority={item.priority}
					disabled={pending}
					onChange={priority => onSetPriority(item, priority)}
				/>
				<AssigneeMenu assignee={item.assignee} disabled={pending} onChange={user => onReassign(item, user)} />
				<div className={css(styleSheet.moreMenu)} onClick={event => event.stopPropagation()}>
					<Dropdown>
						<DropdownEllipsisTrigger aria-label='More actions' disabled={pending} />
						<DropdownContent align='end' styles={[styleSheet.menu]}>
							<DropdownItem onClick={() => onFocus(item)}>Open in Focus mode</DropdownItem>
							<DropdownItem onClick={() => onEdit(item)}>Edit</DropdownItem>
							{!isKeepInTouch ? (
								<DropdownItem styles={[styleSheet.deleteItem]} onClick={() => onDelete(item)}>
									Delete
								</DropdownItem>
							) : null}
						</DropdownContent>
					</Dropdown>
				</div>
			</div>
			{pending ? (
				<Button variant='ghost' size='sm' disabled styles={[styleSheet.pendingLabel]}>
					Saving…
				</Button>
			) : null}
		</animated.div>
	);
};

const ENTRANCE_KEYFRAMES = {
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
};

const styleSheet = StyleSheet.create({
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minHeight: 52,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px ${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		cursor: 'pointer',
		minWidth: 0,
		animationName: [ENTRANCE_KEYFRAMES],
		animationDuration: '180ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'both',
		':last-child': {
			borderBottom: 'none',
		},
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
		':hover [data-slot="row-select"]': {
			opacity: 1,
		},
		':hover [data-slot="row-grip"]': {
			opacity: 1,
		},
	},
	rowSelected: {
		backgroundColor: THEME.colors.primary.light,
	},
	rowDragging: {
		opacity: 0.4,
	},
	rowPending: {
		cursor: 'default',
	},
	selectCell: {
		display: 'flex',
		alignItems: 'center',
		opacity: 0,
		transition: 'opacity 120ms ease-out',
	},
	selectCellVisible: {
		opacity: 1,
	},
	grip: {
		display: 'inline-flex',
		color: THEME.colors.icon.disabled,
		cursor: 'grab',
		flexShrink: 0,
		opacity: 0,
		transition: 'opacity 120ms ease-out',
	},
	completeCell: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	completeCheckbox: {
		width: 20,
		height: 20,
		borderRadius: 6,
	},
	completeHigh: {
		borderColor: THEME.colors.danger.main,
		boxShadow: `0 0 0 2px ${THEME.colors.danger.light}`,
	},
	completeMedium: {
		borderColor: THEME.colors.warning.main,
		boxShadow: `0 0 0 2px ${THEME.colors.warning.light}`,
	},
	completeLow: {
		borderColor: THEME.colors.primary.main,
		boxShadow: `0 0 0 2px ${THEME.colors.primary.light}`,
	},
	textCell: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minWidth: 0,
		gap: 2,
	},
	title: {
		fontSize: THEME.typography.fontSizes.md,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
		transition: 'color 200ms ease-out',
	},
	titleCompleting: {
		textDecoration: 'line-through',
		color: THEME.colors.text.light,
	},
	secondary: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	metaCell: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		flexShrink: 0,
		[THEME.queries.md]: {
			flexWrap: 'wrap',
			justifyContent: 'flex-end',
			maxWidth: '50%',
		},
	},
	noteIndicator: {
		color: THEME.colors.icon.gray,
		display: 'inline-flex',
		flexShrink: 0,
		padding: `0 ${SPACE[4]}px`,
	},
	moreMenu: {
		marginLeft: 2,
	},
	menu: {
		minWidth: 200,
		whiteSpace: 'nowrap',
	},
	deleteItem: {
		color: THEME.colors.danger.dark,
	},
	pendingLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
	},
});
